import { useEffect, useMemo, useState } from "react";
import type { SystemSettingDto } from "@codestack/shared";
import { changePasswordSchema } from "@codestack/shared";
import { Badge, Button, Input, Textarea } from "@codestack/ui";
import { apiRequest, ApiError, authApi } from "../lib/api";
import { useApi } from "../lib/useApi";
import { useAuth } from "../lib/auth";
import { ErrorState, InlineAlert, LoadingState } from "../components/States";
import { dateTime } from "../lib/format";

type SettingSource = "db" | "env" | "missing";

type SquadStatus = {
  baseUrl: string;
  hasSecretKey: boolean;
  hasPublicKey: boolean;
  sources?: {
    baseUrl: SettingSource;
    secretKey: SettingSource;
    publicKey: SettingSource;
  };
};

type SquadCheckStatus = "ok" | "warn" | "fail";

type SquadCheck = {
  id: string;
  label: string;
  status: SquadCheckStatus;
  detail: string;
};

type SquadTestResult = {
  ok: boolean;
  summary: string;
  latencyMs: number | null;
  checks: SquadCheck[];
  testedAt: string;
};

const CHECK_TONE: Record<SquadCheckStatus, "success" | "warning" | "danger"> = {
  ok: "success",
  warn: "warning",
  fail: "danger",
};

type Draft = Record<string, string>;

/**
 * Squad issues exactly two keys. There is no merchant ID and no separate
 * webhook secret — Squad signs webhooks with the secret key itself.
 */
const SQUAD_KEYS = ["squad.baseUrl", "squad.secretKey", "squad.publicKey"] as const;

const LABELS: Record<string, string> = {
  "squad.baseUrl": "API base URL",
  "squad.secretKey": "Secret key",
  "squad.publicKey": "Public key",
};

const SECRET_KEYS = new Set(["squad.secretKey", "squad.publicKey"]);

type SourceField = "baseUrl" | "secretKey" | "publicKey";

/** Maps a setting key to the matching entry in `SquadStatus.sources`. */
const SOURCE_FIELD: Record<string, SourceField> = {
  "squad.baseUrl": "baseUrl",
  "squad.secretKey": "secretKey",
  "squad.publicKey": "publicKey",
};

export function SettingsPage() {
  const { can, user } = useAuth();
  const settings = useApi<SystemSettingDto[]>("/admin/settings");
  const squad = useApi<SquadStatus>("/admin/settings/squad");

  const [draft, setDraft] = useState<Draft>({});
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ tone: "success" | "danger"; text: string } | null>(
    null,
  );

  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<SquadTestResult | null>(null);
  const [testError, setTestError] = useState<string | null>(null);

  const [liveChat, setLiveChat] = useState("");
  const [liveChatSaving, setLiveChatSaving] = useState(false);
  const [liveChatMessage, setLiveChatMessage] = useState<{
    tone: "success" | "danger";
    text: string;
  } | null>(null);

  const [contactPhone, setContactPhone] = useState("");
  const [contactAddress, setContactAddress] = useState("");
  const [contactSaving, setContactSaving] = useState(false);
  const [contactMessage, setContactMessage] = useState<{
    tone: "success" | "danger";
    text: string;
  } | null>(null);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordMessage, setPasswordMessage] = useState<string | null>(null);
  const [passwordSaving, setPasswordSaving] = useState(false);

  const canManage = can("settings:manage");

  const existing = useMemo(() => {
    const map = new Map<string, SystemSettingDto>();
    for (const entry of settings.data ?? []) map.set(entry.key, entry);
    return map;
  }, [settings.data]);

  useEffect(() => {
    if (!settings.data) return;
    // Secret values are never returned by the API, so the draft starts blank for
    // them and an empty field means "leave unchanged". Only overwrite a field
    // the operator has not typed into yet — otherwise the reload that follows a
    // save would clear the key that was just entered, making it look as though
    // the save silently dropped it.
    setDraft((current) => {
      const next: Draft = { ...current };
      for (const key of SQUAD_KEYS) {
        const entry = existing.get(key);
        if (entry?.isSecret) {
          if (next[key] === undefined) next[key] = "";
        } else {
          next[key] = entry?.value ?? "";
        }
      }
      return next;
    });
    setLiveChat((current) => current || (existing.get("site.liveChatSnippet")?.value ?? ""));
    setContactPhone((current) => current || (existing.get("site.phone")?.value ?? ""));
    setContactAddress((current) => current || (existing.get("site.address")?.value ?? ""));
  }, [settings.data, existing]);

  /**
   * Phone and address are optional: the public site hides them when blank, so
   * saving an empty value is a legitimate way to remove them.
   */
  const saveContactDetails = async () => {
    setContactSaving(true);
    setContactMessage(null);
    try {
      await apiRequest("/admin/settings", {
        method: "PUT",
        body: {
          settings: [
            { key: "site.phone", value: contactPhone.trim(), isSecret: false },
            { key: "site.address", value: contactAddress.trim(), isSecret: false },
          ],
        },
      });
      setContactMessage({
        tone: "success",
        text:
          contactPhone.trim() || contactAddress.trim()
            ? "Contact details saved. Reload the public site to see them."
            : "Contact details cleared. The public site no longer shows a phone number or address.",
      });
      settings.reload();
    } catch (cause) {
      setContactMessage({
        tone: "danger",
        text:
          cause instanceof ApiError ? cause.message : "The contact details could not be saved.",
      });
    } finally {
      setContactSaving(false);
    }
  };

  const saveLiveChat = async () => {
    setLiveChatSaving(true);
    setLiveChatMessage(null);
    try {
      const snippet = liveChat.trim();
      await apiRequest("/admin/settings", {
        method: "PUT",
        body: {
          settings: [{ key: "site.liveChatSnippet", value: snippet, isSecret: false }],
        },
      });
      // A snippet without a <script> tag is almost always a copy/paste mistake
      // (the provider's dashboard shows the loader inside a code block, and it
      // is easy to grab only the surrounding markup). Say so rather than
      // reporting a success that will never render a widget.
      const hasScript = /<script[\s>]/i.test(snippet);
      setLiveChatMessage({
        tone: snippet && !hasScript ? "danger" : "success",
        text: !snippet
          ? "Live chat removed from the public site."
          : hasScript
            ? "Live chat saved. Reload the public site to see it."
            : "Saved, but the snippet contains no <script> tag, so nothing will load. Paste the full embed code from your provider.",
      });
      settings.reload();
    } catch (cause) {
      setLiveChatMessage({
        tone: "danger",
        text: cause instanceof ApiError ? cause.message : "The live chat could not be saved.",
      });
    } finally {
      setLiveChatSaving(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const payload = SQUAD_KEYS.map((key) => ({
        key,
        value: draft[key] ?? "",
        isSecret: SECRET_KEYS.has(key),
      })).filter((entry) => !(entry.isSecret && entry.value === ""));

      await apiRequest("/admin/settings", { method: "PUT", body: { settings: payload } });
      setMessage({ tone: "success", text: "Settings saved." });
      settings.reload();
      squad.reload();
    } catch (cause) {
      setMessage({
        tone: "danger",
        text: cause instanceof ApiError ? cause.message : "Settings could not be saved.",
      });
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async () => {
    setTesting(true);
    setTestError(null);
    setTestResult(null);
    try {
      const result = await apiRequest<SquadTestResult>("/admin/settings/squad/test", {
        method: "POST",
      });
      setTestResult(result);
    } catch (cause) {
      setTestError(
        cause instanceof ApiError ? cause.message : "The connection test could not be run.",
      );
    } finally {
      setTesting(false);
    }
  };

  const changePassword = async (event: React.FormEvent) => {
    event.preventDefault();
    setPasswordError(null);
    setPasswordMessage(null);

    if (newPassword !== confirmPassword) {
      setPasswordError("The new passwords do not match.");
      return;
    }

    const parsed = changePasswordSchema.safeParse({ currentPassword, newPassword });
    if (!parsed.success) {
      setPasswordError(parsed.error.issues[0]?.message ?? "Check the password requirements.");
      return;
    }

    setPasswordSaving(true);
    try {
      await authApi.changePassword(currentPassword, newPassword);
      setPasswordMessage("Password updated. Other sessions have been revoked.");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (cause) {
      setPasswordError(
        cause instanceof ApiError ? cause.message : "The password could not be changed.",
      );
    } finally {
      setPasswordSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-display text-xl font-bold text-white">Settings</h1>
        <p className="text-sm text-ink-400">
          Payment provider configuration and your account security.
        </p>
      </div>

      <section className="admin-card p-5">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="font-display text-sm font-semibold text-white">Squad configuration</h2>
            <p className="mt-1 text-xs text-ink-500">
              Secrets are encrypted at rest and never returned to the browser.
            </p>
          </div>
          {squad.data ? (
            <div className="flex flex-wrap gap-1.5">
              <Badge tone={squad.data.hasSecretKey ? "success" : "warning"}>
                Secret key {squad.data.hasSecretKey ? "set" : "missing"}
                {squad.data.sources?.secretKey === "env" ? " (env)" : ""}
              </Badge>
              <Badge tone={squad.data.hasPublicKey ? "success" : "warning"}>
                Public key {squad.data.hasPublicKey ? "set" : "missing"}
                {squad.data.sources?.publicKey === "env" ? " (env)" : ""}
              </Badge>
            </div>
          ) : null}
        </div>

        {settings.error ? <ErrorState message={settings.error} onRetry={settings.reload} /> : null}
        {settings.loading && !settings.data ? (
          <div className="mt-4">
            <LoadingState label="Loading settings…" />
          </div>
        ) : null}

        {settings.data ? (
          <div className="mt-5 space-y-4">
            {message ? <InlineAlert tone={message.tone}>{message.text}</InlineAlert> : null}

            <div className="grid gap-4 sm:grid-cols-2">
              {SQUAD_KEYS.map((key) => {
                const isSecret = SECRET_KEYS.has(key);
                const record = existing.get(key);
                const sourceField: SourceField | undefined = SOURCE_FIELD[key];
                const source = sourceField ? squad.data?.sources?.[sourceField] : undefined;
                // A blank secret box does not mean "no key": the value may be
                // coming from the Coolify environment. Say which, so the
                // operator does not re-enter a key that is already working.
                const hint =
                  source === "env"
                    ? "Loaded from the environment (SQUAD_* variable). Saving a value here overrides it."
                    : source === "db" && record
                      ? `Saved in admin · last updated ${dateTime(record.updatedAt)}`
                      : record
                        ? `Last updated ${dateTime(record.updatedAt)}`
                        : "Not configured yet";
                return (
                  <Input
                    key={key}
                    label={LABELS[key] ?? key}
                    type={isSecret ? "password" : "text"}
                    value={draft[key] ?? ""}
                    placeholder={
                      isSecret && (record || source === "env")
                        ? "•••••••• (leave blank to keep current)"
                        : undefined
                    }
                    disabled={!canManage}
                    onChange={(event) =>
                      setDraft((current) => ({ ...current, [key]: event.target.value }))
                    }
                    hint={hint}
                  />
                );
              })}
            </div>

            {canManage ? (
              <div className="flex flex-wrap items-center justify-end gap-2">
                <Button
                  variant="secondary"
                  onClick={testConnection}
                  disabled={testing || saving}
                >
                  {testing ? "Testing…" : "Test connection"}
                </Button>
                <Button onClick={saveSettings} disabled={saving || testing}>
                  {saving ? "Saving…" : "Save configuration"}
                </Button>
              </div>
            ) : (
              <p className="text-xs text-ink-500">
                You do not have permission to change system settings.
              </p>
            )}

            {testError ? <InlineAlert tone="danger">{testError}</InlineAlert> : null}

            {testResult ? (
              <div className="rounded-lg border border-white/[0.08] bg-white/[0.02] p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-xs font-semibold text-white">
                    {testResult.ok ? "Connection test passed" : "Connection test failed"}
                  </p>
                  <span className="text-[11px] text-ink-500">
                    {dateTime(testResult.testedAt)}
                    {testResult.latencyMs !== null ? ` · ${testResult.latencyMs} ms` : ""}
                  </span>
                </div>
                <p className="mt-1 text-xs text-ink-400">{testResult.summary}</p>

                <ul className="mt-3 space-y-2">
                  {testResult.checks.map((check) => (
                    <li key={check.id} className="flex items-start gap-2.5">
                      <Badge tone={CHECK_TONE[check.status]} className="mt-0.5 shrink-0">
                        {check.status === "ok" ? "OK" : check.status === "warn" ? "Warn" : "Fail"}
                      </Badge>
                      <div className="min-w-0">
                        <p className="text-xs font-medium text-ink-200">{check.label}</p>
                        <p className="break-words text-[11px] text-ink-500">{check.detail}</p>
                      </div>
                    </li>
                  ))}
                </ul>

                <p className="mt-3 text-[11px] text-ink-500">
                  Tests the saved configuration — save any edits above before testing.
                </p>
              </div>
            ) : null}
          </div>
        ) : null}
      </section>

      <section className="admin-card p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="font-display text-sm font-semibold text-white">Contact details</h2>
            <p className="mt-1 text-xs text-ink-500">
              Shown on the public website's footer, contact page and legal pages. Both fields
              are optional — leave them empty to hide them from visitors.
            </p>
          </div>
          <Badge tone={contactPhone.trim() || contactAddress.trim() ? "success" : "neutral"}>
            {contactPhone.trim() || contactAddress.trim() ? "Published" : "Not shown"}
          </Badge>
        </div>

        {contactMessage ? (
          <div className="mt-4">
            <InlineAlert tone={contactMessage.tone}>{contactMessage.text}</InlineAlert>
          </div>
        ) : null}

        <div className="mt-4 space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Input
              label="Phone number"
              type="tel"
              value={contactPhone}
              onChange={(event) => setContactPhone(event.target.value)}
              placeholder="+234 700 000 0000"
              hint="Include the country code so international visitors can dial it."
              disabled={!canManage}
            />
            <Input
              label="Office address"
              value={contactAddress}
              onChange={(event) => setContactAddress(event.target.value)}
              placeholder="12 Example Street, Victoria Island, Lagos, Nigeria"
              hint="Used in the footer and in the contact section of the legal pages."
              disabled={!canManage}
            />
          </div>

          {canManage ? (
            <div className="flex flex-wrap items-center justify-end gap-2">
              <Button
                variant="secondary"
                onClick={() => {
                  setContactPhone("");
                  setContactAddress("");
                  setContactMessage(null);
                }}
                disabled={contactSaving || (!contactPhone && !contactAddress)}
              >
                Clear
              </Button>
              <Button onClick={saveContactDetails} disabled={contactSaving}>
                {contactSaving ? "Saving…" : "Save contact details"}
              </Button>
            </div>
          ) : (
            <p className="text-xs text-ink-500">
              You do not have permission to change system settings.
            </p>
          )}
        </div>
      </section>

      <section className="admin-card p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="font-display text-sm font-semibold text-white">Live chat</h2>
            <p className="mt-1 text-xs text-ink-500">
              Paste the full embed snippet from your provider (Tawk.to, Crisp, Intercom…).
              It is injected into the public website on every page load.
            </p>
          </div>
          <Badge tone={liveChat.trim() ? "success" : "neutral"}>
            {liveChat.trim() ? "Enabled" : "Disabled"}
          </Badge>
        </div>

        {liveChatMessage ? (
          <div className="mt-4">
            <InlineAlert tone={liveChatMessage.tone}>{liveChatMessage.text}</InlineAlert>
          </div>
        ) : null}

        <div className="mt-4 space-y-4">
          <Textarea
            label="Embed code"
            value={liveChat}
            onChange={(event) => setLiveChat(event.target.value)}
            rows={10}
            spellCheck={false}
            placeholder={'<script type="text/javascript">\n  // paste your provider snippet here\n</script>'}
            className="font-mono text-[11px]"
            disabled={!canManage}
          />
          <p className="text-[11px] text-ink-500">
            Leave empty to disable live chat. The snippet is served to visitors as-is, so only
            paste code you trust.
          </p>

          {canManage ? (
            <div className="flex flex-wrap items-center justify-end gap-2">
              <Button
                variant="secondary"
                onClick={() => {
                  setLiveChat("");
                  setLiveChatMessage(null);
                }}
                disabled={liveChatSaving || liveChat.length === 0}
              >
                Clear
              </Button>
              <Button onClick={saveLiveChat} disabled={liveChatSaving}>
                {liveChatSaving ? "Saving…" : "Save live chat"}
              </Button>
            </div>
          ) : (
            <p className="text-xs text-ink-500">
              You do not have permission to change system settings.
            </p>
          )}
        </div>
      </section>

      <section className="admin-card p-5">
        <h2 className="font-display text-sm font-semibold text-white">Your account</h2>
        <p className="mt-1 text-xs text-ink-500">
          Signed in as <span className="text-ink-300">{user?.email}</span>
        </p>

        <form onSubmit={changePassword} className="mt-5 max-w-md space-y-4">
          {passwordError ? <InlineAlert tone="danger">{passwordError}</InlineAlert> : null}
          {passwordMessage ? <InlineAlert tone="success">{passwordMessage}</InlineAlert> : null}

          <Input
            label="Current password"
            type="password"
            value={currentPassword}
            onChange={(event) => setCurrentPassword(event.target.value)}
            required
          />
          <Input
            label="New password"
            type="password"
            value={newPassword}
            onChange={(event) => setNewPassword(event.target.value)}
            hint="At least 12 characters."
            required
          />
          <Input
            label="Confirm new password"
            type="password"
            value={confirmPassword}
            onChange={(event) => setConfirmPassword(event.target.value)}
            required
          />

          <Button type="submit" disabled={passwordSaving}>
            {passwordSaving ? "Updating…" : "Change password"}
          </Button>
        </form>
      </section>
    </div>
  );
}
